import { useCallback, useEffect, useMemo, useState } from 'react';

import { getWhatsNewRegainConfig, updateWhatsNewRegainConfig } from '../../api/WhatsNewRegain';
import { readApiErrorMessage } from './apiError';
import { WhatsNewAuditMetadata, WhatsNewComingSoonItem, WhatsNewUpdate } from './types';
import { findDuplicateIds, toInstant, upsertById } from './utils';

export type ListName = 'updates' | 'coming_soon';

export interface Toast {
  severity: 'success' | 'error';
  message: string;
}

const LOAD_ERROR_FALLBACK = "Could not load the What's New configuration. Please try again.";
const SAVE_ERROR_FALLBACK = "Could not save the What's New configuration. Please try again.";

/**
 * `PUT /whats-new/admin` replaces the whole stored document rather than merging,
 * so the page must never save from a state it did not read back from the server.
 * `configLoaded` is the gate for that: it only turns true once a GET has returned
 * a usable config, and the page keeps Add and Save disabled until it does.
 *
 * It is never set back to false. Once a good config is in state there is a real
 * baseline to edit, so a later failed reload should surface the error without
 * throwing away work the admin has already done.
 */
export const useWhatsNewConfig = () => {
  const [updates, setUpdates] = useState<WhatsNewUpdate[]>([]);
  const [comingSoon, setComingSoon] = useState<WhatsNewComingSoonItem[]>([]);
  const [metadata, setMetadata] = useState<WhatsNewAuditMetadata | null>(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [configLoaded, setConfigLoaded] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setLoadError('');

    const response = await getWhatsNewRegainConfig();
    const config = response?.data?.body;

    // A 200 without an `updates` array means the response is not a config we can
    // safely edit, so treat it as a failed load rather than as an empty config.
    if (response?.status !== 200 || !Array.isArray(config?.updates)) {
      setLoadError(readApiErrorMessage(response, LOAD_ERROR_FALLBACK));
      setLoading(false);
      return;
    }

    setUpdates(config.updates);
    setComingSoon(config.coming_soon ?? []);
    setMetadata(config.metadata ?? null);
    setConfigLoaded(true);
    setIsDirty(false);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const saveUpdate = useCallback((update: WhatsNewUpdate) => {
    setUpdates(previous => upsertById(previous, update));
    setIsDirty(true);
  }, []);

  const saveComingSoon = useCallback((item: WhatsNewComingSoonItem) => {
    setComingSoon(previous => upsertById(previous, item));
    setIsDirty(true);
  }, []);

  const removeEntry = useCallback((list: ListName, id: string) => {
    if (list === 'updates') {
      setUpdates(previous => previous.filter(item => item.id !== id));
    } else {
      setComingSoon(previous => previous.filter(item => item.id !== id));
    }
    setIsDirty(true);
  }, []);

  const publish = useCallback(async () => {
    // Belt and braces against the overwrite: the buttons are already disabled
    // while `configLoaded` is false, but the save path refuses too.
    if (!configLoaded) {
      return;
    }

    const duplicateIds = [
      ...findDuplicateIds(updates.map(item => item.id)),
      ...findDuplicateIds(comingSoon.map(item => item.id))
    ];

    if (duplicateIds.length) {
      setToast({
        severity: 'error',
        message: `Duplicate ids are not allowed: ${duplicateIds.join(', ')}`
      });
      return;
    }

    setSaving(true);
    const response = await updateWhatsNewRegainConfig({ updates, coming_soon: comingSoon });
    setSaving(false);

    if (response?.status !== 200) {
      setToast({ severity: 'error', message: readApiErrorMessage(response, SAVE_ERROR_FALLBACK) });
      return;
    }

    const config = response.data?.body ?? {};
    setUpdates(config.updates ?? updates);
    setComingSoon(config.coming_soon ?? comingSoon);
    setMetadata(config.metadata ?? null);
    setIsDirty(false);
    setToast({ severity: 'success', message: "What's New configuration saved." });
  }, [configLoaded, updates, comingSoon]);

  /**
   * Display order only. `updates` keeps the order the server sent, and that is
   * what `publish` writes back, so saving never reshuffles the stored document.
   * REGAIN sorts by publishedAt on its own client and ignores array order.
   */
  const sortedUpdates = useMemo(
    () => [...updates].sort((a, b) => toInstant(b.published_at) - toInstant(a.published_at)),
    [updates]
  );

  return {
    updates,
    sortedUpdates,
    comingSoon,
    metadata,
    loading,
    saving,
    loadError,
    configLoaded,
    isDirty,
    toast,
    setToast,
    fetchConfig,
    saveUpdate,
    saveComingSoon,
    removeEntry,
    publish
  };
};

export default useWhatsNewConfig;
